import { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { sortArrayBy } from 'functions/utilities'

import useLoadEntities from 'hooks/useLoadEntities'
import useSetState from 'hooks/useSetState'
import useWatchEntityUpdates from 'hooks/useWatchEntityUpdates'

const watchEntityKeys = [
  'images',
]

const filterEntityImages = (entityType, entityId, images) => {
  const filtered = Object.values(images).filter(image => (
    image
      && image.imageable_type
      && image.imageable_type === entityType
      && image.imageable_id
      && image.imageable_id === entityId
  ))

  return filtered
}

const getSelectedEntity = (entities, entityType, entityId) => {
  const {
    campaigns, campaignComparables, imageLibraries, users,
  } = entities

  if (entityType === 'Campaign'){
    return campaigns[entityId] || {}
  }

  if (entityType === 'CampaignComparable'){
    return campaignComparables[entityId] || {}
  }

  if (entityType === 'ImageLibrary'){
    return imageLibraries[entityId] || {}
  }

  if (entityType === 'User'){
    return users[entityId] || {}
  }

  return {}
}

const selectEntity = (entity, setState) => {
  const { id, type } = entity

  setState({
    selectedEntityId: id,
    selectedEntityType: type,
  })
}

const defaultState = {
  selectedEntityId: 0,
  selectedEntityType: 'Campaign',
}

function useImageSelect(options = {}){
  const {
    preselectedEntity: { id: preselectedId, type: preselectedType },
    specifiedImageIds,
  } = options || {}

  const {
    updatedEntities: {
      images: imagesUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const [state, setState] = useSetState(defaultState)
  const { selectedEntityType, selectedEntityId } = state

  // Load Entities
  const { loading: loadingImageLibraries } = useLoadEntities('agency', 'imageLibraries', {}, [selectedEntityType], {
    shouldPerformFn: (entityReducer) => {
      const { loading, loaded, errors } = entityReducer
      return selectedEntityType === 'ImageLibrary' && !loading && !loaded && !errors.length
    },
  })

  const { loading: loadingUsers } = useLoadEntities('agency', 'users', {}, [selectedEntityType], {
    shouldPerformFn: (entityReducer) => {
      const { loading, loaded, errors } = entityReducer
      return selectedEntityType === 'User' && !loading && !loaded && !errors.length
    },
  })

  const entities = useSelector(reduxState => reduxState.entities)
  const { images } = entities

  // Preselect Entity
  useEffect(() => {
    if (preselectedType && preselectedId){
      setState({
        selectedEntityId: preselectedId,
        selectedEntityType: preselectedType,
      })
    }
  }, [preselectedType, preselectedId])

  // Load Entity Images
  useLoadEntities('agency', 'images', { include_image_sizes: true }, [selectedEntityType, selectedEntityId], {
    actionName: 'loadEntityImages',
    dispatchAction: (action, requestOptions) => action(selectedEntityType, selectedEntityId, requestOptions),
    shouldPerformFn: (entityReducer) => {
      const { loading, loadedImagesForEntities } = entityReducer
      const entityKey = `${selectedEntityType}${selectedEntityId}`

      return (
        selectedEntityType !== 'Campaign'
        && selectedEntityId
        && !loading
        && !loadedImagesForEntities.includes(entityKey)
      )
    },
  })

  // Filter Entity Images
  const filteredImages = useMemo(() => {
    const filtered = filterEntityImages(selectedEntityType, selectedEntityId, images)
    const sorted = sortArrayBy(filtered, 'asc', 'sort')
    return sorted
  }, [selectedEntityType, selectedEntityId, imagesUpdatedAt])

  const selectedEntity = getSelectedEntity(entities, selectedEntityType, selectedEntityId)

  // Specified Images
  const specifiedImages = useMemo(() => {
    if (specifiedImageIds){
      const mapped = specifiedImageIds.map(imageId => images[imageId])
        .filter(i => i)

      return mapped
    }

    return []
  }, [JSON.stringify(specifiedImageIds), imagesUpdatedAt])

  const hasImages = !!filteredImages.length
  const hasSpecifiedImages = !!specifiedImages.length
  const loading = loadingImageLibraries || loadingUsers

  return {
    callbacks: {
      selectEntity: library => selectEntity(library, setState),
      setState,
    },
    filteredImages,
    hasImages,
    hasSpecifiedImages,
    images,
    loading,
    selectedEntity,
    selectedEntityId,
    selectedEntityType,
    specifiedImages,
  }
}

export default useImageSelect
